import React, { useState } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { projects, type Project } from '../data/site';
import { ProjectLightbox } from './ProjectLightbox';
import { Reveal } from './Reveal';
import { SectionHeading } from './SectionHeading';
type CardProps = {
  project: Project;
  onOpen: (p: Project) => void;
  className?: string;
  imageClassName?: string;
  size?: 'lg' | 'md';
};
function ProjectCard({
  project,
  onOpen,
  className = '',
  imageClassName = '',
  size = 'md'
}: CardProps) {
  return <button type="button" onClick={() => onOpen(project)} aria-label={`View ${project.name} project`} className={['group relative block w-full overflow-hidden bg-navy-900 text-left', className].join(' ')} data-id="element-146" data-mp="2vexj3eah17bu">
      <img src={project.image} alt={`${project.name} — ${project.service} in ${project.location}`} className={['w-full object-cover transition duration-[900ms] ease-out group-hover:scale-[1.06]', imageClassName].join(' ')} data-id="element-147" data-mp="2me7q0iclhv1t" />
      <span className="absolute inset-0 bg-[linear-gradient(180deg,rgba(5,13,24,0)_35%,rgba(5,13,24,0.88)_100%)] transition duration-500 group-hover:bg-[linear-gradient(180deg,rgba(5,13,24,0.15)_0%,rgba(5,13,24,0.92)_100%)]" aria-hidden="true" data-id="element-148" data-mp="1vj3l5i79dvds" />
      <span className="absolute inset-x-0 bottom-0 block p-6 sm:p-8" data-id="element-149" data-mp="1ml9r6dwyvdts">
        <span className="mb-4 block h-px w-10 bg-brand-500 transition-all duration-500 group-hover:w-24" aria-hidden="true" data-id="element-150" data-mp="1rgguhkmbqgi6" />
        <span className="block text-[10px] font-semibold uppercase tracking-[0.24em] text-brand-400" data-id="element-151" data-mp="1crau3tbr2k8p">
          {project.type}
        </span>
        <span className={['mt-2 block font-heading font-extrabold uppercase leading-tight tracking-tight text-white', size === 'lg' ? 'text-2xl sm:text-4xl' : 'text-lg sm:text-2xl'].join(' ')} data-id="element-152" data-mp="13t8pl3st59go">
          {project.name}
        </span>
        <span className="mt-1.5 block text-sm text-white/50" data-id="element-153" data-mp="ytm9mmllhf46">{project.location}</span>
        <span className="mt-5 inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-white opacity-0 transition-all duration-500 group-hover:opacity-100" data-id="element-154" data-mp="30ia2pzuoh8ol">
          View Project
          <ArrowUpRight className="h-4 w-4" aria-hidden="true" data-id="element-155" data-mp="2rposj8544abd" />
        </span>
      </span>
    </button>;
}
export function FeaturedProjects() {
  const [active, setActive] = useState<Project | null>(null);
  const [featured, second, third, wide, vertical] = projects;
  return <section className="w-full bg-navy py-24 lg:py-32" data-id="element-156" data-mp="3fg2dyft6zawz">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8" data-id="element-157" data-mp="3fgauqisfr0dg">
        <Reveal data-id="element-158" data-mp="3afwu3lt5yspg">
          <SectionHeading label="Featured Work" tone="dark" title="Built to be seen." description="A closer look at recent projects — the structures, surfaces, and details our crews delivered across Middle Tennessee." data-id="element-159" data-mp="3afmz1jt9hvcw" />
        </Reveal>

        <div className="mt-16 grid gap-4 lg:grid-cols-12" data-id="element-160" data-mp="1u1eqit5btmk9">
          <Reveal className="lg:col-span-7" data-id="element-161" data-mp="1jyjaa3t0qel6">
            <ProjectCard project={featured} onOpen={setActive} size="lg" imageClassName="h-[420px] sm:h-[560px] lg:h-[620px]" data-id="element-162" data-mp="1jzua5sjcf270" />
          </Reveal>

          <div className="grid gap-4 lg:col-span-5" data-id="element-163" data-mp="1tv4b0cy093j7">
            <Reveal delay={0.08} data-id="element-164" data-mp="1ynxk1d0iay13">
              <ProjectCard project={second} onOpen={setActive} imageClassName="h-[260px] sm:h-[300px]" data-id="element-165" data-mp="1yoyubf9p7ghr" />
            </Reveal>
            <Reveal delay={0.16} data-id="element-166" data-mp="22r3ku54unx06">
              <ProjectCard project={third} onOpen={setActive} imageClassName="h-[260px] sm:h-[300px]" data-id="element-167" data-mp="27nkknwlevzt3" />
            </Reveal>
          </div>

          <Reveal delay={0.08} className="lg:col-span-8" data-id="element-168" data-mp="2cnq6gw502s39">
            <ProjectCard project={wide} onOpen={setActive} imageClassName="h-[300px] sm:h-[380px]" data-id="element-169" data-mp="2hmtcaist12ca" />
          </Reveal>
          <Reveal delay={0.16} className="lg:col-span-4" data-id="element-170" data-mp="ia7e00pttzot">
            <ProjectCard project={vertical} onOpen={setActive} imageClassName="h-[300px] sm:h-[380px]" data-id="element-171" data-mp="n88vih7ltqy6" />
          </Reveal>
        </div>
      </div>

      <ProjectLightbox project={active} onClose={() => setActive(null)} data-id="element-172" data-mp="2do1bwc6qtvm4" />
    </section>;
}